import React, { useState, useEffect } from 'react';
import { packagesService } from '../services/packagesService';
import DestinationAutocomplete from './DestinationAutocomplete';
import { useApp } from '../context/AppContext';
import {
    XMarkIcon,
    PlusIcon,
    MinusIcon,
    TagIcon,
    DocumentTextIcon,
    PencilIcon,
    CurrencyDollarIcon,
    PhotoIcon,
    TrashIcon,
    InformationCircleIcon,
    DocumentDuplicateIcon
} from '@heroicons/react/24/outline';

const HolidayPackageForm = ({ isOpen, onClose, onSuccess, editPackage = null }) => {
    const { showToast } = useApp();
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        destination: '',
        category: '',
        type: 1,
        actualPrice: '',
        sellingPrice: '',
        duration: '',
        limitPerBooking: '',
        serviceFee: '',
        gst: '',
        inclusions: [],
        highlights: [],
        images: [],
        amenity: [],
        itinerary: [],
        capacityPerday: ''
    });
    const [durationDays, setDurationDays] = useState('');
    const [durationNights, setDurationNights] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [newInclusion, setNewInclusion] = useState('');
    const [newHighlight, setNewHighlight] = useState('');
    const [imageLoading, setImageLoading] = useState(false);
    const [newItineraryItem, setNewItineraryItem] = useState({
        title: '',
        location: '',
        description: '',
        amenities: []
    });
    // Removed destinations state - now using autocomplete component

    // Categories for dropdowns

    const categories = ['Adventure', 'Beach', 'Cultural', 'Wildlife', 'Honeymoon',
        'Family', 'Luxury', 'Budget', 'Religious', 'Wellness']

    const packageTypes = [
        { value: 1, label: 'Non-Schedulable Package' },
        { value: 2, label: 'Schedulable Package' }
    ];

    const amenityOptions = [
        { value: 1, label: 'Air Conditioning' },
        { value: 2, label: 'Wi-Fi' },
        { value: 3, label: 'Smart TV' },
        { value: 4, label: 'Mini Fridge' },
        { value: 5, label: 'Tea & Coffee Maker' },
        { value: 6, label: 'Balcony' },
        { value: 7, label: 'In-Room Safe Locker' },
        { value: 8, label: '24/7 Room Service' },
        { value: 9, label: 'Complimentary Toiletries' },
        { value: 10, label: 'Bathtub / Jacuzzi' },
        { value: 11, label: 'Swimming Pool' },
        { value: 12, label: 'Spa' },
        { value: 13, label: 'Gym' },
        { value: 14, label: 'Restaurant & Café' },
        { value: 15, label: 'Rooftop Bar' },
        { value: 16, label: 'Kids Play Area' },
        { value: 17, label: 'Conference Halls' },
        { value: 18, label: 'Travel Desk' },
        { value: 19, label: 'Laundry' },
        { value: 20, label: 'Valet Parking' }
    ];

    const itineraryAmenityOptions = [
        { value: 21, label: 'Return Flights / Train Tickets' },
        { value: 22, label: 'Private Airport Transfers' },
        { value: 23, label: 'Breakfast Included (Breakfast)' },
        { value: 24, label: 'All-Meals Included' },
        { value: 25, label: 'Guided City Tours' },
        { value: 26, label: 'Personalized Tour Guide' },
        { value: 27, label: 'Welcome Drink' },
        { value: 28, label: 'Candle-Light Dinner' },
        { value: 29, label: 'Travel Insurance' }
    ];

    const isEditMode = !!editPackage;

    // Removed destinations loading useEffect - now using autocomplete component

    // Default form data
    const getDefaultFormData = () => ({
        title: '',
        description: '',
        destination: '',
        category: '',
        type: 1,
        actualPrice: '',
        sellingPrice: '',
        duration: '',
        limitPerBooking: '',
        serviceFee: '',
        gst: '',
        inclusions: [],
        highlights: [],
        images: [],
        amenity: [],
        itinerary: [],
        capacityPerday: ''
    });

    // Load package data when in edit mode
    useEffect(() => {
        if (isOpen) {
            if (editPackage) {
                // Set form data from the passed package data
                setFormData({
                    title: editPackage.title || '',
                    description: editPackage.description || '',
                    destination: editPackage.destination || '',
                    category: editPackage.category || '',
                    type: editPackage.type || 1,
                    actualPrice: editPackage.actualPrice || '',
                    sellingPrice: editPackage.sellingPrice || '',
                    duration: editPackage.duration || '',
                    limitPerBooking: editPackage.limitPerBooking || editPackage.capacity || '',
                    serviceFee: editPackage.serviceFee || '',
                    gst: editPackage.gst || '',
                    inclusions: editPackage.inclusions || [],
                    highlights: editPackage.highlights || [],
                    images: editPackage.images || [],
                    amenity: editPackage.amenity || [],
                    itinerary: editPackage.itinerary || [],
                    capacityPerday: editPackage.capacityPerday || ''
                });
                // Parse existing duration format (e.g., "2N/3D")
                const duration = editPackage.duration || '';
                if (duration.includes('N') && duration.includes('D')) {
                    const nightsMatch = duration.match(/(\d+)N/);
                    const daysMatch = duration.match(/(\d+)D/);
                    setDurationNights(nightsMatch ? nightsMatch[1] : '');
                    setDurationDays(daysMatch ? daysMatch[1] : '');
                } else {
                    setDurationNights('');
                    setDurationDays('');
                }
            } else {
                // Reset form for create mode
                setFormData(getDefaultFormData());
                setDurationDays('');
                setDurationNights('');
            }
            setError(null);
            setFieldErrors({}); // Clear all field errors when form opens
        }
    }, [isOpen, editPackage]);

    const handleInputChange = (field, value) => {
        // Check for type conversion validation
        if (field === 'type' && isEditMode && editPackage) {
            const oldType = editPackage.type;
            const newType = parseInt(value);
            
            // If converting from Type 2 to Type 1, check for date slots
            if (oldType === 2 && newType === 1) {
                const hasDateSlots = editPackage.dateSlots && editPackage.dateSlots.length > 0;
                if (hasDateSlots) {
                    setError('You need to remove all date slots before converting from Type 2 to Type 1.');
                    return;
                }
            }
        }
        
        // Enforce character limits
        if (field === 'title' && value.length > 50) {
            return; // Don't update if exceeding limit
        }
        if (field === 'description' && value.length > 500) {
            return; // Don't update if exceeding limit
        }
        
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Validate the field as user types
        validateField(field, value);
    };

    const handleAddInclusion = () => {
        if (newInclusion.trim()) {
            const newInclusions = [...(formData.inclusions || []), newInclusion.trim()];
            setFormData(prev => ({
                ...prev,
                inclusions: newInclusions
            }));
            setNewInclusion('');
            // Clear validation error once minimum reached
            if (newInclusions.length >= 2) {
                setFieldErrors(prev => ({ ...prev, inclusions: '' }));
            }
        }
    };

    const handleRemoveInclusion = (index) => {
        const newInclusions = (formData.inclusions || []).filter((_, i) => i !== index);
        setFormData(prev => ({
            ...prev,
            inclusions: newInclusions
        }));
        // Trigger validation after removal
        validateField('inclusions', newInclusions);
    };

    const handleAddHighlight = () => {
        if (newHighlight.trim()) {
            const newHighlights = [...(formData.highlights || []), newHighlight.trim()];
            setFormData(prev => ({
                ...prev,
                highlights: newHighlights
            }));
            setNewHighlight('');
            // Clear validation error once minimum reached
            if (newHighlights.length >= 2) {
                setFieldErrors(prev => ({ ...prev, highlights: '' }));
            }
        }
    };

    const handleRemoveHighlight = (index) => {
        const newHighlights = (formData.highlights || []).filter((_, i) => i !== index);
        setFormData(prev => ({
            ...prev,
            highlights: newHighlights
        }));
        // Trigger validation after removal
        validateField('highlights', newHighlights);
    };

    // Amenity handlers
    const handleAmenityChange = (amenityValue, checked) => {
        setFormData(prev => ({
            ...prev,
            amenity: checked 
                ? [...(prev.amenity || []), amenityValue]
                : (prev.amenity || []).filter(a => a !== amenityValue)
        }));
    };

    // Itinerary handlers
    const handleItineraryInputChange = (field, value) => {
        setNewItineraryItem(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleItineraryAmenityChange = (amenityValue, checked) => {
        setNewItineraryItem(prev => ({
            ...prev,
            amenities: checked 
                ? [...(prev.amenities || []), amenityValue]
                : (prev.amenities || []).filter(a => a !== amenityValue)
        }));
    };

    const handleAddItineraryItem = () => {
        if (newItineraryItem.title && newItineraryItem.location && newItineraryItem.description) {
            // Validate that itinerary count doesn't exceed nights
            const nightsCount = parseInt(durationNights) || 0;
            if (nightsCount > 0 && (formData.itinerary?.length || 0) >= nightsCount) {
                setError(`Itinerary items cannot exceed the number of nights (${nightsCount})`);
                return;
            }
            
            setFormData(prev => ({
                ...prev,
                itinerary: [...(prev.itinerary || []), { ...newItineraryItem }]
            }));
            setNewItineraryItem({
                title: '',
                location: '',
                description: '',
                amenities: []
            });
        }
    };

    const handleCopyItineraryItem = (item) => {
        // Validate that itinerary count doesn't exceed nights when duplicating
        const nightsCount = parseInt(durationNights) || 0;
        if (nightsCount > 0 && (formData.itinerary?.length || 0) >= nightsCount) {
            setError(`Itinerary items cannot exceed the number of nights (${nightsCount})`);
            return;
        }
        
        // Pre-fill the form with the copied item's data for easy editing
        setNewItineraryItem({
            title: item.title,
            location: item.location,
            description: item.description,
            amenities: item.amenities || []
        });
        
        showToast('success', 'Itinerary item copied. Please review and edit if needed.');
        
        // Scroll to the itinerary form after a brief delay
        setTimeout(() => {
            const itineraryForm = document.querySelector('[placeholder="Title (e.g., Sharanam resort)"]');
            if (itineraryForm) {
                itineraryForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
                itineraryForm.focus();
            }
        }, 100);
    };

    const handleRemoveItineraryItem = (index) => {
        setFormData(prev => ({
            ...prev,
            itinerary: (prev.itinerary || []).filter((_, i) => i !== index)
        }));
    };


    // Image upload functions
    const handleImageUpload = async (event) => {
        const files = event.target.files;
        const currentImages = formData.images || [];
        const totalImages = currentImages.length + files.length;

        if (totalImages > 10) {
            setError('You can upload a maximum of 10 images.');
            return;
        }

        if (imageLoading) {
            setError('Please wait for current upload to complete.');
            return;
        }

        if (files && files.length > 0) {
            setImageLoading(true); // Start loader
            setError(null); // Clear previous errors
            
            // Validate files before processing
            const validFiles = [];
            
            for (const file of Array.from(files)) {
                // Accept all image types - check if it's an image file
                if (!file.type.startsWith('image/')) {
                    setError(`File ${file.name} is not an image file. Please upload an image.`);
                    setImageLoading(false);
                    continue;
                }
                
                // Check file size (max 10MB)
                const maxSize = 10 * 1024 * 1024; // 10MB
                if (file.size > maxSize) {
                    setError(`File ${file.name} is too large (${(file.size / 1024 / 1024).toFixed(2)} MB). Maximum size allowed is 10 MB.`);
                    setImageLoading(false);
                    continue;
                }
                
                // Check minimum file size (at least 1KB)
                if (file.size < 1024) {
                    setError(`File ${file.name} is too small. Please use a valid image file.`);
                    setImageLoading(false);
                    continue;
                }

                // Check image dimensions for rectangular format
                try {
                    const image = new Image();
                    const url = URL.createObjectURL(file);
                    let dimensionError = '';
                    
                    await new Promise((resolve, reject) => {
                        image.onload = () => {
                            URL.revokeObjectURL(url);
                            
                            // Check if image is rectangular (width > height)
                            if (image.width <= image.height) {
                                dimensionError = `File ${file.name} has improper dimensions. Required: Rectangular format (landscape orientation). Current dimensions: ${image.width}x${image.height}`;
                                reject(new Error(dimensionError));
                                return;
                            }
                            
                            // Check minimum dimensions
                            if (image.width < 400 || image.height < 300) {
                                dimensionError = `File ${file.name} has improper dimensions. Minimum size required: 400x300 pixels. Current dimensions: ${image.width}x${image.height}`;
                                reject(new Error(dimensionError));
                                return;
                            }
                            
                            resolve();
                        };
                        
                        image.onerror = () => {
                            URL.revokeObjectURL(url);
                            dimensionError = `File ${file.name} could not be loaded. Please check if it's a valid image file.`;
                            reject(new Error(dimensionError));
                        };
                        
                        image.src = url;
                    });
                    
                    // If we reach here, image is valid
                    validFiles.push(file);
                    
                } catch (error) {
                    // Show dimension error
                    setError(error.message);
                    setImageLoading(false);
                    continue;
                }
            }

            if (validFiles.length > 0) {
                // Clear any previous errors
                setError(null);
                // Process files one by one to avoid overwhelming the server
                await processFilesSequentially(validFiles);
            } else {
                setImageLoading(false); // Stop loader if no valid files
            }
        }
    };

    const processFilesSequentially = async (files) => {
        for (const file of files) {
            const reader = new FileReader();
            
            await new Promise((resolve) => {
                reader.onload = (e) => {
                    const image = new Image();
                    image.onload = () => {
                        uploadImageToAWS(file).then(resolve).catch(() => {
                            setImageLoading(false);
                            resolve();
                        });
                    };
                    image.onerror = () => {
                        setError(`Failed to load image: ${file.name}`);
                        setImageLoading(false);
                        resolve();
                    };
                    image.src = e.target.result;
                };
                reader.onerror = () => {
                    setError(`Failed to read file: ${file.name}`);
                    setImageLoading(false);
                    resolve();
                };
                reader.readAsDataURL(file);
            });
            
            // Wait for current upload to complete before processing next
            while (imageLoading) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }
    };

    const uploadImageToAWS = async (file) => {
        try {
            // Generate a proper timestamp without quotes and ensure it's valid
            const now = new Date();
            const currentYear = now.getFullYear();
            
            // Check if system date is in the future (which can cause AWS signature issues)
            let timestamp;
            if (currentYear > 2024) {
                console.warn('System date is in the future:', currentYear, '- This may cause AWS signature issues');
                // Use a more conservative timestamp for AWS compatibility
                const adjustedDate = new Date(2024, now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds());
                timestamp = adjustedDate.toISOString().replace(/[:.]/g, '-');
                console.log('Using adjusted timestamp for AWS compatibility:', timestamp);
            } else {
                timestamp = now.toISOString().replace(/[:.]/g, '-');
                console.log('Using current timestamp:', timestamp);
            }
            const fileExtension = file.name.split('.').pop().toLowerCase();
            
            // Ensure we have a valid file extension
            const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
            const finalExtension = validExtensions.includes(fileExtension) ? fileExtension : 'jpg';
            
            const filename = `${timestamp}_${Math.random().toString(36).substring(2, 15)}.${finalExtension}`;
            
            // Determine MIME type based on file extension if file.type is not reliable
            let mimeType = file.type;
            if (!mimeType || !mimeType.startsWith('image/')) {
                switch (finalExtension) {
                    case 'jpg':
                    case 'jpeg':
                        mimeType = 'image/jpeg';
                        break;
                    case 'png':
                        mimeType = 'image/png';
                        break;
                    case 'gif':
                        mimeType = 'image/gif';
                        break;
                    case 'webp':
                        mimeType = 'image/webp';
                        break;
                    default:
                        mimeType = 'image/jpeg';
                }
            }
            
            const payload = {
                filename: filename,
                mimetype: mimeType,
                directory: 'vendor-package-images',
            };

            console.log('Uploading image with payload:', payload);
            console.log('Current date:', now);
            console.log('File details:', {
                name: file.name,
                size: file.size,
                type: file.type,
                finalExtension: finalExtension,
                mimeType: mimeType
            });

            const response = await packagesService.getAwsImageLinks(payload);
            
            console.log('AWS response:', response);
            
            if (!response) {
                throw new Error('No response from server');
            }
            
            if (!response.uploadUrl) {
                console.error('Missing uploadUrl in response:', response);
                throw new Error('Invalid response from server - missing upload URL');
            }

            console.log('Got upload URL, uploading file...');
            console.log('Upload URL:', response.uploadUrl);
            
            const uploadResult = await packagesService.uploadAwsImage(response.uploadUrl, file);
            console.log('Upload result:', uploadResult);
            
            if (!response.viewUrl) {
                console.error('Missing viewUrl in response:', response);
                throw new Error('Invalid response from server - missing view URL');
            }
            
            setFormData(prev => ({
                ...prev,
                images: [...(prev.images || []), response.viewUrl]
            }));
            setImageLoading(false);
            setError(null); // Clear any previous errors
            // Validate images field after upload
            validateField('images', [...(formData.images || []), response.viewUrl]);
            console.log('Image uploaded successfully');
        } catch (error) {
            console.error('Error uploading image:', error);
            console.error('Error details:', {
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data
            });
            
            // Handle specific error cases
            if (error.message?.includes('session has expired') || error.message?.includes('not authenticated')) {
                setError('Your session has expired. Please refresh the page and login again.');
            } else if (error.response?.status === 410) {
                setError('Your session has expired. Please refresh the page and login again.');
            } else if (error.response?.status === 401) {
                setError('Authentication failed. Please refresh the page and login again.');
            } else if (error.response?.status === 403) {
                setError('Access denied. Please check your permissions.');
            } else if (error.response?.status === 400) {
                if (error.message?.includes('AWS configuration error') || error.message?.includes('AWS credentials error') || error.message?.includes('AuthorizationQueryParametersError')) {
                    setError('Image upload service is temporarily unavailable due to AWS configuration issues. You can continue without images or try again later.');
                } else {
                    setError('Invalid file format or size. Please try a different image.');
                }
            } else if (error.response?.status === 500) {
                setError('Server error. Please try again later.');
            } else if (error.message?.includes('Invalid response from server')) {
                setError('Server error. Please try again.');
            } else if (error.message?.includes('Network Error')) {
                setError('Network error. Please check your connection and try again.');
            } else {
                setError(`Failed to upload image: ${error.message || 'Please try again.'}`);
            }
            setImageLoading(false);
        }
    };

    const handleRemoveImage = (index) => {
        const updatedImages = (formData.images || []).filter((_, i) => i !== index);
        setFormData(prev => ({
            ...prev,
            images: updatedImages
        }));
        // Validate images field after removal
        validateField('images', updatedImages);
    };

    const validateField = (fieldName, value) => {
        setFieldErrors(prev => {
            const errors = { ...prev };
            
            switch (fieldName) {
                case 'title':
                    if (!value || value.trim() === '') {
                        errors.title = 'Title is required';
                    } else {
                        delete errors.title;
                    }
                    break;
                case 'description':
                    if (!value || value.trim() === '') {
                        errors.description = 'Description is required';
                    } else {
                        delete errors.description;
                    }
                    break;
                case 'destination':
                    if (!value || value.trim() === '') {
                        errors.destination = 'Destination is required';
                    } else {
                        delete errors.destination;
                    }
                    break;
                case 'category':
                    if (!value || value.trim() === '') {
                        errors.category = 'Category is required';
                    } else {
                        delete errors.category;
                    }
                    break;
                case 'actualPrice':
                    if (!value || isNaN(value) || parseFloat(value) <= 0) {
                        errors.actualPrice = 'Valid actual price is required';
                    } else {
                        // Check if actual price is greater than selling price
                        const sellingPriceValue = parseFloat(formData.sellingPrice);
                        if (!isNaN(sellingPriceValue) && parseFloat(value) <= sellingPriceValue) {
                            errors.actualPrice = 'Actual price must be greater than selling price';
                        } else {
                            delete errors.actualPrice;
                        }
                    }
                    break;
                case 'sellingPrice':
                    if (!value || isNaN(value) || parseFloat(value) <= 0) {
                        errors.sellingPrice = 'Valid selling price is required';
                    } else {
                        // Check if selling price is less than actual price
                        const actualPriceValue = parseFloat(formData.actualPrice);
                        if (!isNaN(actualPriceValue) && parseFloat(value) >= actualPriceValue) {
                            errors.sellingPrice = 'Selling price must be less than actual price';
                        } else {
                            delete errors.sellingPrice;
                        }
                    }
                    break;
                case 'limitPerBooking':
                    if (!value || isNaN(value) || parseInt(value) <= 0) {
                        errors.limitPerBooking = 'Limit Per Booking is required';
                    } else if (formData.type === 1 && formData.capacityPerday && parseInt(value) >= parseInt(formData.capacityPerday)) {
                        errors.limitPerBooking = 'Limit per booking must be less than capacity per day';
                    } else {
                        delete errors.limitPerBooking;
                    }
                    break;
                case 'capacityPerday':
                    if (formData.type === 1 && (!value || isNaN(value) || parseInt(value) <= 0)) {
                        errors.capacityPerday = 'Capacity per day is required for Non-Schedulable packages';
                    } else if (formData.type === 1 && value && formData.limitPerBooking && parseInt(value) <= parseInt(formData.limitPerBooking)) {
                        errors.capacityPerday = 'Capacity per day must be greater than limit per booking';
                    } else {
                        delete errors.capacityPerday;
                    }
                    break;
                case 'serviceFee':
                    if (!value || isNaN(value) || parseFloat(value) < 0) {
                        errors.serviceFee = 'Valid service fee is required';
                    } else {
                        delete errors.serviceFee;
                    }
                    break;
                case 'gst':
                    if (!value || isNaN(value) || parseFloat(value) < 0) {
                        errors.gst = 'Valid GST percentage is required';
                    } else {
                        delete errors.gst;
                    }
                    break;
                case 'duration':
                    if (formData.type === 1 && (!value || value.trim() === '')) {
                        errors.duration = 'Duration is required for Type 1 packages';
                    } else {
                        delete errors.duration;
                    }
                    break;
                case 'images':
                    if (!value || value.length === 0) {
                        errors.images = 'Please upload at least one image for the package';
                    } else {
                        delete errors.images;
                    }
                    break;
                case 'inclusions':
                    if (!formData.inclusions || formData.inclusions.length < 2) {
                        errors.inclusions = 'Please add at least 2 inclusions';
                    } else {
                        delete errors.inclusions;
                    }
                    break;
                case 'highlights':
                    if (!formData.highlights || formData.highlights.length < 2) {
                        errors.highlights = 'Please add at least 2 highlights';
                    } else {
                        delete errors.highlights;
                    }
                    break;
                default:
                    break;
            }
            
            return errors;
        });
        
        // Return validation result
        const currentErrors = fieldErrors;
        return !currentErrors[fieldName];
    };



    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate all fields and collect errors
        const fieldsToValidate = ['title', 'description', 'destination', 'category', 'actualPrice', 'sellingPrice', 'limitPerBooking', 'serviceFee', 'gst', 'duration', 'images', 'inclusions', 'highlights'];
        const validationErrors = {};
        let hasErrors = false;
        
        fieldsToValidate.forEach(field => {
            let value = formData[field];
            // For inclusions and highlights, validate the array length
            if (field === 'inclusions' || field === 'highlights') {
                value = formData[field] || [];
            }
            let isValid = true;
            let errorMessage = '';
            
            switch (field) {
                case 'title':
                    if (!value || value.trim() === '') {
                        isValid = false;
                        errorMessage = 'Title is required';
                    }
                    break;
                case 'description':
                    if (!value || value.trim() === '') {
                        isValid = false;
                        errorMessage = 'Description is required';
                    }
                    break;
                case 'destination':
                    if (!value || value.trim() === '') {
                        isValid = false;
                        errorMessage = 'Destination is required';
                    }
                    break;
                case 'category':
                    if (!value || value.trim() === '') {
                        isValid = false;
                        errorMessage = 'Category is required';
                    }
                    break;
                case 'actualPrice':
                    if (!value || isNaN(value) || parseFloat(value) <= 0) {
                        isValid = false;
                        errorMessage = 'Valid actual price is required';
                    } else {
                        // Check if actual price is greater than selling price
                        const sellingPriceValue = parseFloat(formData.sellingPrice);
                        if (!isNaN(sellingPriceValue) && parseFloat(value) <= sellingPriceValue) {
                            isValid = false;
                            errorMessage = 'Actual price must be greater than selling price';
                        }
                    }
                    break;
                case 'sellingPrice':
                    if (!value || isNaN(value) || parseFloat(value) <= 0) {
                        isValid = false;
                        errorMessage = 'Valid selling price is required';
                    } else {
                        // Check if selling price is less than actual price
                        const actualPriceValue = parseFloat(formData.actualPrice);
                        if (!isNaN(actualPriceValue) && parseFloat(value) >= actualPriceValue) {
                            isValid = false;
                            errorMessage = 'Selling price must be less than actual price';
                        }
                    }
                    break;
                case 'limitPerBooking':
                    if (!value || isNaN(value) || parseInt(value) <= 0) {
                        isValid = false;
                        errorMessage = 'Limit Per Booking is required';
                    } else if (formData.type === 1 && formData.capacityPerday && parseInt(value) >= parseInt(formData.capacityPerday)) {
                        isValid = false;
                        errorMessage = 'Limit per booking must be less than capacity per day';
                    }
                    break;
                case 'capacityPerday':
                    if (formData.type === 1 && (!value || isNaN(value) || parseInt(value) <= 0)) {
                        isValid = false;
                        errorMessage = 'Capacity per day is required for Non-Schedulable packages';
                    } else if (formData.type === 1 && value && formData.limitPerBooking && parseInt(value) <= parseInt(formData.limitPerBooking)) {
                        isValid = false;
                        errorMessage = 'Capacity per day must be greater than limit per booking';
                    }
                    break;
                case 'serviceFee':
                    if (!value || isNaN(value) || parseFloat(value) < 0) {
                        isValid = false;
                        errorMessage = 'Valid service fee is required';
                    }
                    break;
                case 'gst':
                    if (!value || isNaN(value) || parseFloat(value) < 0) {
                        isValid = false;
                        errorMessage = 'Valid GST percentage is required';
                    }
                    break;
                case 'duration':
                    if (formData.type === 1 && (!durationNights || !durationDays || durationNights.trim() === '' || durationDays.trim() === '')) {
                        isValid = false;
                        errorMessage = 'Duration (nights and days) is required for Type 1 packages';
                        break;
                    }
                    // Enforce days = nights + 1
                    if (formData.type === 1) {
                        const nNum = parseInt(durationNights);
                        const dNum = parseInt(durationDays);
                        if (!isNaN(nNum) && !isNaN(dNum) && dNum !== nNum + 1) {
                            isValid = false;
                            errorMessage = 'Days must be Nights + 1 (e.g., 2N/3D)';
                        }
                    }
                    break;
                case 'images':
                    if (!value || value.length === 0) {
                        isValid = false;
                        errorMessage = 'Please upload at least one image for the package';
                    }
                    break;
                case 'inclusions':
                    if (!value || value.length < 2) {
                        isValid = false;
                        errorMessage = 'Please add at least 2 inclusions';
                    }
                    break;
                case 'highlights':
                    if (!value || value.length < 2) {
                        isValid = false;
                        errorMessage = 'Please add at least 2 highlights';
                    }
                    break;
                default:
                    // No validation needed for this field
                    break;
            }
            
            if (!isValid) {
                validationErrors[field] = errorMessage;
                hasErrors = true;
            }
        });

        // Set all validation errors at once
        setFieldErrors(validationErrors);

        if (hasErrors) {
            return;
        }

        try {
            setLoading(true);
            setError(null);

            let response;
            // Combine duration days and nights into format like "2N/3D"
            const durationValue = durationNights && durationDays 
                ? `${durationNights}N/${durationDays}D` 
                : formData.duration;
            
            if (isEditMode) {
                // Send all fields for update including type and inclusions
                const updateData = {
                    title: formData.title,
                    description: formData.description,
                    destination: formData.destination,
                    category: formData.category,
                    type: formData.type,
                    actualPrice: parseFloat(formData.actualPrice),
                    sellingPrice: parseFloat(formData.sellingPrice),
                    duration: durationValue,
                    limitPerBooking: parseInt(formData.limitPerBooking),
                    capacityPerday: formData.type === 1 ? parseInt(formData.capacityPerday) : undefined,
                    serviceFee: parseFloat(formData.serviceFee),
                    gst: parseFloat(formData.gst),
                    inclusions: formData.inclusions || [],
                    highlights: formData.highlights || [],
                    images: formData.images || [],
                    amenity: formData.amenity || [],
                    itinerary: formData.itinerary || []
                };
                response = await packagesService.updateHolidayPackage(editPackage._id, updateData);
            } else {
                // Include all fields for create
                const createData = {
                    ...formData,
                    duration: durationValue,
                    actualPrice: parseFloat(formData.actualPrice),
                    sellingPrice: parseFloat(formData.sellingPrice),
                    limitPerBooking: parseInt(formData.limitPerBooking),
                    capacityPerday: formData.type === 1 ? parseInt(formData.capacityPerday) : undefined,
                    serviceFee: parseFloat(formData.serviceFee),
                    gst: parseFloat(formData.gst),
                    images: formData.images || [],
                    amenity: formData.amenity || [],
                    itinerary: formData.itinerary || []
                };
                response = await packagesService.createHolidayPackage(createData);
            }

            if (response.success) {
                // Show success toast
                showToast('success', response.message || `Package ${isEditMode ? 'updated' : 'created'} successfully`);
                
                // Reset form
                setFormData(getDefaultFormData());
                onSuccess && onSuccess(response.data, isEditMode);
                onClose();
            } else {
                const errorMessage = response.message || `Failed to ${isEditMode ? 'update' : 'create'} package`;
                
                // If it's an edit mode and there's a specific error, close sidebar and show toast
                if (isEditMode && errorMessage) {
                    showToast('error', errorMessage);
                    onClose(); // Close the sidebar
                    return;
                }
                
                // For other errors, show in form
                setError(errorMessage);
                showToast('error', errorMessage);
            }
        } catch (error) {
            console.error(`Error ${isEditMode ? 'updating' : 'creating'} holiday package:`, error);
            
            // Extract error message from response if available
            const errorMessage = error.response?.data?.message || error.message || `Failed to ${isEditMode ? 'update' : 'create'} package. Please try again.`;
            
            // If it's an edit mode, close sidebar and show toast
            if (isEditMode) {
                showToast('error', errorMessage);
                onClose(); // Close the sidebar
                return;
            }
            
            // For create mode, show error in form
            setError(errorMessage);
            showToast('error', errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const isFormValid = () => {
        const requiredFields = ['title', 'description', 'destination', 'category', 'actualPrice', 'sellingPrice', 'limitPerBooking', 'serviceFee', 'gst', 'images'];
        
        // Check basic required fields
        for (const field of requiredFields) {
            const value = formData[field];
            if (!value || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0)) {
                return false;
            }
        }
        
        // Check numeric fields
        const actualPrice = parseFloat(formData.actualPrice);
        const sellingPrice = parseFloat(formData.sellingPrice);
        if (isNaN(actualPrice) || actualPrice <= 0) return false;
        if (isNaN(sellingPrice) || sellingPrice <= 0) return false;
        // Check that actual price is greater than selling price
        if (actualPrice <= sellingPrice) return false;
        if (isNaN(formData.limitPerBooking) || parseInt(formData.limitPerBooking) <= 0) return false;
        if (formData.type === 1 && (isNaN(formData.capacityPerday) || parseInt(formData.capacityPerday) <= 0)) return false;
        
        // Additional validation: capacity per day must be greater than limit per booking
        if (formData.type === 1 && formData.capacityPerday && formData.limitPerBooking && 
            parseInt(formData.capacityPerday) <= parseInt(formData.limitPerBooking)) {
            return false;
        }
        if (isNaN(formData.serviceFee) || parseFloat(formData.serviceFee) < 0) return false;
        if (isNaN(formData.gst) || parseFloat(formData.gst) < 0) return false;
        
        // Check duration for Type 1 packages
        if (formData.type === 1 && (!durationNights || !durationDays || durationNights.trim() === '' || durationDays.trim() === '')) {
            return false;
        }
        
        // Enforce days = nights + 1
        if (formData.type === 1 && durationNights && durationDays) {
            const nNum = parseInt(durationNights);
            const dNum = parseInt(durationDays);
            if (!isNaN(nNum) && !isNaN(dNum) && dNum !== nNum + 1) {
                return false;
            }
        }
        
        return true;
    };

    const handleClose = () => {
        if (!loading) {
            // Don't clear form data - just close the sidebar
            setError(null);
            onClose();
        }
    };

    const handleBackdropClick = (e) => {
        // Prevent closing when clicking outside
        e.preventDefault();
        e.stopPropagation();
        // Do nothing - sidebar stays open
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleBackdropClick} />

            {/* Sidebar */}
            <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl">
                <div className="flex h-full flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                        <div className="flex items-center">
                            {isEditMode ? (
                                <PencilIcon className="h-6 w-6 text-primary-600 mr-3" />
                            ) : (
                                <DocumentTextIcon className="h-6 w-6 text-primary-600 mr-3" />
                            )}
                            <h2 className="text-lg font-semibold text-gray-900">
                                {isEditMode ? 'Edit Holiday Package' : 'Add Holiday Package'}
                            </h2>
                        </div>
                        <button
                            onClick={handleClose}
                            disabled={loading}
                            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Form */}
                    <form key={isOpen ? 'open' : 'closed'} onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6">


                        <div className="space-y-6">
                            {/* Title */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Title <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => handleInputChange('title', e.target.value)}
                                    maxLength={50}
                                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                                        fieldErrors.title ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="Enter package title (max 50 characters)"
                                    required
                                />
                                <div className="mt-1 text-right">
                                    <span className="text-xs text-gray-500">
                                        {formData.title.length}/50 characters
                                    </span>
                                </div>
                                {fieldErrors.title && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors.title}</p>
                                )}
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    maxLength={500}
                                    rows={3}
                                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                                        fieldErrors.description ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="Enter package description (max 500 characters)"
                                    required
                                />
                                <div className="mt-1 text-right">
                                    <span className="text-xs text-gray-500">
                                        {formData.description.length}/500 characters
                                    </span>
                                </div>
                                {fieldErrors.description && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors.description}</p>
                                )}
                            </div>

                            {/* Destination */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Destination <span className="text-red-500">*</span>
                                </label>
                                <DestinationAutocomplete
                                        value={formData.destination}
                                    onChange={(value) => handleInputChange('destination', value)}
                                    error={fieldErrors.destination}
                                    disabled={false}
                                />
                                {fieldErrors.destination && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors.destination}</p>
                                )}
                            </div>

                             {/* Category */}
                             <div>
                                 <label className="block text-sm font-medium text-gray-700 mb-2">
                                     Category <span className="text-red-500">*</span>
                                 </label>
                                 <div className="relative">
                                     <TagIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                     <select
                                         value={formData.category}
                                         onChange={(e) => handleInputChange('category', e.target.value)}
                                         className={`w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none ${
                                             fieldErrors.category ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                                         }`}
                                         required
                                     >
                                         <option value="">Select category</option>
                                         {categories.map((cat) => (
                                             <option key={cat} value={cat}>{cat}</option>
                                         ))}
                                     </select>
                                 </div>
                                 {fieldErrors.category && (
                                     <p className="mt-1 text-sm text-red-600">{fieldErrors.category}</p>
                                 )}
                             </div>

                            {/* Actual Price */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Actual Price <span className="text-red-500">*</span> (Vendor cost)
                                </label>
                                <div className="relative">
                                    <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        type="number"
                                        value={formData.actualPrice}
                                        onChange={(e) => handleInputChange('actualPrice', e.target.value)}
                                        className={`w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                                            fieldErrors.actualPrice ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="Enter actual price"
                                        min="0"
                                        step="0.01"
                                        required
                                    />
                                </div>
                                {fieldErrors.actualPrice && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors.actualPrice}</p>
                                )}
                            </div>

                            {/* Selling Price */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Selling Price <span className="text-red-500">*</span> (Price shown to user)
                                </label>
                                <div className="relative">
                                    <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        type="number"
                                        value={formData.sellingPrice}
                                        onChange={(e) => handleInputChange('sellingPrice', e.target.value)}
                                        className={`w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                                            fieldErrors.sellingPrice ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="Enter selling price"
                                        min="0"
                                        step="0.01"
                                        required
                                    />
                                </div>
                                {fieldErrors.sellingPrice && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors.sellingPrice}</p>
                                )}
                            </div>

                            {/* Service Fee */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Service Fee <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        type="number"
                                        value={formData.serviceFee}
                                        onChange={(e) => handleInputChange('serviceFee', e.target.value)}
                                        className={`w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                                            fieldErrors.serviceFee ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="Enter service fee"
                                        min="0"
                                        step="0.01"
                                        required
                                    />
                                </div>
                                {fieldErrors.serviceFee && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors.serviceFee}</p>
                                )}
                            </div>

                            {/* GST */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    GST (%) <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        type="number"
                                        value={formData.gst}
                                        onChange={(e) => handleInputChange('gst', e.target.value)}
                                        className={`w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                                            fieldErrors.gst ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="Enter GST percentage"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        required
                                    />
                                </div>
                                {fieldErrors.gst && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors.gst}</p>
                                )}
                            </div>

                            {/* Limit Per Booking */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Limit Per Booking <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    value={formData.limitPerBooking}
                                    onChange={(e) => {
                                        handleInputChange('limitPerBooking', e.target.value);
                                        // Also validate capacityPerday if it exists
                                        if (formData.capacityPerday) {
                                            handleInputChange('capacityPerday', formData.capacityPerday);
                                        }
                                    }}
                                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                                        fieldErrors.limitPerBooking ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="Enter Limit Per Booking"
                                    min="1"
                                    required
                                />
                                {fieldErrors.limitPerBooking && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors.limitPerBooking}</p>
                                )}
                            </div>

                            {/* Image Upload */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Upload Images (Max 10) <span className="text-red-500">*</span>
                                </label>
                                <p className="text-xs text-gray-500 mb-2">
                                    Images must be in rectangular format (landscape orientation) with minimum dimensions of 400x300 pixels.
                                </p>
                                <input
                                    type="file"
                                    id="imageUpload"
                                    accept="image/*"
                                    multiple
                                    onChange={handleImageUpload}
                                    disabled={imageLoading}
                                    className="hidden"
                                />
                                <label
                                    htmlFor="imageUpload"
                                    className={`flex items-center justify-center w-full px-4 py-3 border-2 border-dashed rounded-lg transition-colors ${
                                        imageLoading 
                                            ? 'border-primary-400 bg-primary-50 cursor-wait' 
                                            : fieldErrors.images 
                                                ? 'border-red-300 bg-red-50 hover:border-red-400 cursor-pointer' 
                                                : 'border-gray-300 hover:border-primary-500 hover:bg-primary-50 cursor-pointer'
                                    }`}
                                >
                                    <div className="flex flex-col items-center">
                                        {imageLoading ? (
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-2"></div>
                                        ) : (
                                            <PhotoIcon className="h-8 w-8 text-gray-400 mb-2" />
                                        )}
                                        <span className="text-sm text-gray-600">
                                            {imageLoading ? 'Uploading...' : 'Choose Images'}
                                        </span>
                                        <span className="text-xs text-gray-500 mt-1">
                                            {(formData.images || []).length}/10 images selected
                                        </span>
                                    </div>
                                </label>
                                {fieldErrors.images && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors.images}</p>
                                )}
                                {error && (
                                    <div className="mt-2 bg-red-50 border border-red-300 rounded-md p-3">
                                        <div className="flex items-start">
                                            <XMarkIcon className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                                            <p className="text-sm text-red-800">{error}</p>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Display uploaded images */}
                                {(formData.images || []).length > 0 && (
                                    <div className="mt-4 grid grid-cols-2 gap-2">
                                        {(formData.images || []).map((imageUrl, index) => (
                                            <div key={index} className="relative group">
                                                <img
                                                    src={imageUrl}
                                                    alt={`Upload ${index + 1}`}
                                                    className="w-full h-24 object-cover rounded-lg border border-gray-200"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveImage(index)}
                                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Duration - Nights and Days */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Duration <span className="text-red-500">*</span>
                                </label>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs text-gray-600 mb-1">Nights</label>
                                        <input
                                            type="number"
                                            value={durationNights}
                                            onChange={(e) => {
                                                const nights = e.target.value;
                                                setDurationNights(nights);
                                                
                                                // Validate that days = nights + 1 (only if both values are valid numbers)
                                                if (nights && durationDays) {
                                                    const nightsNum = parseInt(nights);
                                                    const daysNum = parseInt(durationDays);
                                                    if (!isNaN(nightsNum) && !isNaN(daysNum) && daysNum !== nightsNum + 1) {
                                                        setFieldErrors(prev => ({
                                                            ...prev,
                                                            duration: `Days must be Nights + 1 (e.g., ${nights}N/${nightsNum + 1}D)`
                                                        }));
                                                        return;
                                                    }
                                                }
                                                // Clear error if validation passes
                                                setFieldErrors(prev => ({
                                                    ...prev,
                                                    duration: ''
                                                }));
                                            }}
                                            min="1"
                                            className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''} ${
                                                fieldErrors.duration ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., 2"
                                            disabled={isEditMode}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-600 mb-1">Days</label>
                                        <input
                                            type="number"
                                            value={durationDays}
                                            onChange={(e) => {
                                                const days = e.target.value;
                                                setDurationDays(days);
                                                
                                                // Validate that days = nights + 1 (only if both values are valid numbers)
                                                if (durationNights && days) {
                                                    const nightsNum = parseInt(durationNights);
                                                    const daysNum = parseInt(days);
                                                    if (!isNaN(nightsNum) && !isNaN(daysNum) && daysNum !== nightsNum + 1) {
                                                        setFieldErrors(prev => ({
                                                            ...prev,
                                                            duration: `Days must be Nights + 1 (e.g., ${durationNights}N/${nightsNum + 1}D)`
                                                        }));
                                                        return;
                                                    }
                                                }
                                                // Clear error if validation passes
                                                setFieldErrors(prev => ({
                                                    ...prev,
                                                    duration: ''
                                                }));
                                            }}
                                            min="1"
                                            className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''} ${
                                                fieldErrors.duration ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., 3"
                                            disabled={isEditMode}
                                        />
                                    </div>
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    Format: {durationNights && durationDays ? `${durationNights}N/${durationDays}D` : 'e.g., 2N/3D'}
                                </p>
                                {fieldErrors.duration && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors.duration}</p>
                                )}
                                {isEditMode && (
                                    <p className="mt-1 text-xs text-gray-500">Duration cannot be changed in edit mode</p>
                                )}
                            </div>

                            {/* Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Package Type
                                </label>
                                <select
                                    value={formData.type}
                                    onChange={(e) => handleInputChange('type', parseInt(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                    disabled={isEditMode && formData.status === 'PUBLISHED'}
                                >
                                    {packageTypes.map((type) => (
                                        <option key={type.value} value={type.value}>{type.label}</option>
                                    ))}
                                </select>
                                {isEditMode && formData.status === 'PUBLISHED' && (
                                    <p className="mt-1 text-xs text-gray-500">Cannot change type when package is published</p>
                                )}
                            </div>

                            {/* Capacity Per Day - Only for Type 1 (Non-Schedulable Package) */}
                            {formData.type === 1 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Capacity Per Day <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.capacityPerday}
                                        onChange={(e) => {
                                            handleInputChange('capacityPerday', e.target.value);
                                            // Also validate limitPerBooking if it exists
                                            if (formData.limitPerBooking) {
                                                handleInputChange('limitPerBooking', formData.limitPerBooking);
                                            }
                                        }}
                                        className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                                            fieldErrors.capacityPerday ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="Enter capacity per day"
                                        min="1"
                                        required
                                    />
                                    {fieldErrors.capacityPerday && (
                                        <p className="mt-1 text-sm text-red-600">{fieldErrors.capacityPerday}</p>
                                    )}
                                </div>
                            )}

                            {/* Inclusions */}
                            <div>
                                <label className="flex items-center gap-2 block text-sm font-medium text-gray-700 mb-2">
                                    Inclusions <span className="text-red-500">*</span>
                                    <span className="text-xs text-gray-500">(minimum of 2 is mandatory)</span>
                                    <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-primary-600" title="Items or services included in the package (e.g., accommodation, meals, transportation)" />
                                </label>
                                <div className="space-y-2">
                                    <div className={`flex space-x-2`}>
                                        <input
                                            type="text"
                                            value={newInclusion}
                                            onChange={(e) => setNewInclusion(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddInclusion())}
                                            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                            placeholder="Add inclusion item"
                                            disabled={isEditMode && formData.status === 'PUBLISHED'}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddInclusion}
                                            className="px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex-shrink-0"
                                            disabled={isEditMode && formData.status === 'PUBLISHED'}
                                        >
                                            <PlusIcon className="h-5 w-5" />
                                        </button>
                                    </div>
                                    {(formData.inclusions || []).map((inclusion, index) => (
                                        <div key={index} className="flex items-start gap-2 bg-gray-50 px-3 py-2 rounded-md min-w-0">
                                            <span className="text-sm text-gray-700 break-words flex-1 min-w-0">{inclusion}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveInclusion(index)}
                                                className="text-red-500 hover:text-red-700 disabled:opacity-50 flex-shrink-0 mt-0.5"
                                                disabled={isEditMode && formData.status === 'PUBLISHED'}
                                            >
                                                <MinusIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                {isEditMode && formData.status === 'PUBLISHED' && (
                                    <p className="mt-1 text-xs text-gray-500">Cannot modify inclusions when package is published</p>
                                )}
                                {fieldErrors.inclusions && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors.inclusions}</p>
                                )}
                            </div>

                            {/* Highlights */}
                            <div>
                                <label className="flex items-center gap-2 block text-sm font-medium text-gray-700 mb-2">
                                    Highlights <span className="text-red-500">*</span>
                                    <span className="text-xs text-gray-500">(minimum of 2 is mandatory)</span>
                                    <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-primary-600" title="Key features or attractions of the package (e.g., beach access, guided tours, cultural experiences)" />
                                </label>
                                <div className="space-y-2">
                                    <div className={`flex space-x-2`}>
                                        <input
                                            type="text"
                                            value={newHighlight}
                                            onChange={(e) => setNewHighlight(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddHighlight())}
                                            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                            placeholder="Add highlight item"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddHighlight}
                                            className="px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex-shrink-0"
                                        >
                                            <PlusIcon className="h-5 w-5" />
                                        </button>
                                    </div>
                                    {(formData.highlights || []).map((highlight, index) => (
                                        <div key={index} className="flex items-start gap-2 bg-gray-50 px-3 py-2 rounded-md min-w-0">
                                            <span className="text-sm text-gray-700 break-words flex-1 min-w-0">{highlight}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveHighlight(index)}
                                                className="text-red-500 hover:text-red-700 flex-shrink-0 mt-0.5"
                                            >
                                                <MinusIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                {fieldErrors.highlights && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors.highlights}</p>
                                )}
                            </div>

                            {/* Amenity Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Amenities
                                </label>
                                <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-md p-3 space-y-2">
                                    {amenityOptions.map((option) => (
                                        <label key={option.value} className="flex items-center hover:bg-gray-50 p-1 rounded cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={(formData.amenity || []).includes(option.value)}
                                                onChange={(e) => handleAmenityChange(option.value, e.target.checked)}
                                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                            />
                                            <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                                        </label>
                                    ))}
                                </div>
                                {formData.amenity && formData.amenity.length > 0 && (
                                    <p className="mt-2 text-xs text-gray-500">
                                        {formData.amenity.length} amenit{formData.amenity.length === 1 ? 'y' : 'ies'} selected
                                    </p>
                                )}
                            </div>

                            {/* Itinerary */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Itinerary
                                </label>
                                <div className="space-y-4">
                                    {/* Add Itinerary Item Form */}
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <h4 className="text-sm font-medium text-gray-700 mb-3">Add Itinerary Item</h4>
                                        <div className="space-y-3">
                                            <input
                                                type="text"
                                                value={newItineraryItem.title}
                                                onChange={(e) => handleItineraryInputChange('title', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                                placeholder="Title (e.g., Sharanam resort)"
                                            />
                                            <input
                                                type="text"
                                                value={newItineraryItem.location}
                                                onChange={(e) => handleItineraryInputChange('location', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                                placeholder="Location"
                                            />
                                            <textarea
                                                value={newItineraryItem.description}
                                                onChange={(e) => handleItineraryInputChange('description', e.target.value)}
                                                rows={2}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                                placeholder="Description"
                                            />
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-2">Amenities for this item:</label>
                                                <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2 space-y-1">
                                                    {itineraryAmenityOptions.map((option) => (
                                                        <label key={option.value} className="flex items-center hover:bg-gray-50 p-1 rounded cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={(newItineraryItem.amenities || []).includes(option.value)}
                                                                onChange={(e) => handleItineraryAmenityChange(option.value, e.target.checked)}
                                                                className="h-3 w-3 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                            />
                                                            <span className="ml-2 text-xs text-gray-600">{option.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                                {newItineraryItem.amenities && newItineraryItem.amenities.length > 0 && (
                                                    <p className="mt-1 text-xs text-gray-500">
                                                        {newItineraryItem.amenities.length} amenit{newItineraryItem.amenities.length === 1 ? 'y' : 'ies'} selected
                                                    </p>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleAddItineraryItem}
                                                className="w-full px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm"
                                            >
                                                Add Itinerary Item
                                            </button>
                                        </div>
                                    </div>

                                    {/* Itinerary Count */}
                                    <div className="text-sm text-gray-600 mb-2">
                                        Items: {(formData.itinerary || []).length} / {durationNights || '?'} nights
                                    </div>

                                    {/* Display Itinerary Items */}
                                    {(formData.itinerary || []).map((item, index) => (
                                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-3">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <h5 className="font-medium text-gray-900">{item.title}</h5>
                                                    <p className="text-sm text-gray-600">{item.location}</p>
                                                    <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                                                    {item.amenities && item.amenities.length > 0 && (
                                                        <div className="mt-2">
                                                            <span className="text-xs text-gray-500">Amenities: </span>
                                                            <span className="text-xs text-gray-600">
                                                                {item.amenities.map(amenity => 
                                                                    itineraryAmenityOptions.find(opt => opt.value === amenity)?.label
                                                                ).join(', ')}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleCopyItineraryItem(item)}
                                                        className="text-gray-500 hover:text-primary-600"
                                                        title="Copy itinerary item"
                                                    >
                                                        <DocumentDuplicateIcon className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveItineraryItem(index)}
                                                        className="text-red-500 hover:text-red-700"
                                                        title="Delete itinerary item"
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>


                        </div>
                    </form>

                    {/* Footer */}
                    <div className="border-t border-gray-200 px-6 py-4">
                        <div className="flex space-x-3">
                            {error && (error.includes('Image upload service is temporarily unavailable') || error.includes('AWS configuration issues')) && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setError('Please upload at least one image for the package');
                                        setImageLoading(false);
                                    }}
                                    className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
                                >
                                    Image upload required
                                </button>
                            )}
                            <button
                                type="submit"
                                onClick={handleSubmit}
                                disabled={loading || !isFormValid()}
                                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center"
                            >
                                {loading ? (
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                ) : (
                                    <>
                                        {isEditMode ? (
                                            <PencilIcon className="h-5 w-5 mr-2" />
                                        ) : (
                                            <DocumentTextIcon className="h-5 w-5 mr-2" />
                                        )}
                                        {isEditMode ? 'Update Package' : 'Create Package'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HolidayPackageForm;
